import React, { useEffect, useState, useMemo } from 'react';
import { cryptoPairLabel } from '../composables/fetchCrypto';
import { isCryptoPortfolio, getCryptoPriceSymbol } from '../utils/portfolioType';
import { formatCryptoQty, formatCryptoUsd, shortenDecimalText, num } from '../utils/cryptoDecimal';
import { useNavigate } from 'react-router-dom';
import AddEditStock from './AddEditStock';
import ConfigTargetRatioStockPopup from './ConfigTargetRatioStockPopup';
import { updateStockInSubPortfolioTwo } from '../composables/fetchAPI';
import { usePortfolio } from '../context/PortfolioContext';
import EditHoldingAllocationModal from './PortfolioV2/EditHoldingAllocationModal';
interface ManagePortfolioV2Props {
  symbol: string; // หุ้นที่ต้องการค้นหา
  portfolios?: any[]; // Allow it to be passed but we use context mostly
  onRefresh?: () => Promise<void>;
  onSelectPortfolio: (portfolioId: string, subPortId: string) => void;
  setUpdateTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  currentPrices?: { [symbol: string]: number };
  onSummaryUpdate?: (data: {
    selected: any,
    allMatches: any[],
    currentPrice: number
  } | null) => void;
}

const ManagePortfolioV2: React.FC<ManagePortfolioV2Props> = ({
  symbol,
  onSelectPortfolio,
  setUpdateTrigger,
  currentPrices,
  onSummaryUpdate
}) => {
  const [selectedSubPortId, setSelectedSubPortId] = useState<string | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isConfigStockPopupOpen, setIsConfigStockPopupOpen] = useState(false);
  const navigate = useNavigate();

  const { userPortfolios: portfolios, refreshUserPortfolios, openTransactionModal, displayMode } = usePortfolio();

  // ใช้ useMemo กรองข้อมูลโดยไม่ต้อง Re-render เปล่าๆ
  const matchingStocks = useMemo(() => {
    const stocks: any[] = [];
    portfolios.forEach((portfolio) => {
      portfolio.subPorts?.forEach((subPort: any) => {
        // ✅ 1. ดึงข้อมูลจาก holdings (V2) หรือ stocks (V1)
        const items = subPort.holdings || subPort.stocks || [];

        // พอร์ตคริปโต: assetSymbol เป็น BTC/USD แต่กราฟเลือกด้วย X:BTCUSD → เทียบด้วย providerSymbol
        const isCrypto = isCryptoPortfolio(portfolio);

        items.forEach((stock: any) => {
          // ✅ 2. เช็คชื่อฟิลด์ symbol ให้ครอบคลุม (V2 ใช้ assetSymbol)
          const sym = isCrypto ? getCryptoPriceSymbol(stock) : (stock.assetSymbol || stock.stockSymbol || stock.symbol);
          const isClosed = stock.holdingStatus === 'CLOSED' || stock.status === 'CLOSED';
          if (sym === symbol && !isClosed) {
            stocks.push({
              ...stock,
              // ✅ 3. Map ฟิลด์กลับไปเป็นชื่อที่ UI ด้านล่างเรียกใช้ จะได้ไม่ต้องแก้ UI
              stockSymbol: sym,
              isCrypto,
              // คริปโตส่งเป็น string ทศนิยมยาว → เก็บค่าเดิมไว้แสดงผล และแปลงเป็นตัวเลขไว้คำนวณบนจอ
              quantityRaw: stock.quantity,
              quantity: num(stock.quantity),
              avgBuyPrice: num(stock.avgEntryPrice || stock.avgBuyPrice || 0),
              expectedRatio: stock.expectedRatio || stock.ratio || 0,
              portId: portfolio.portfolioId || portfolio.portId || portfolio.id,
              portName: portfolio.name,
              subPortId: subPort.subPortId,
              subPortName: subPort.name,
            });
          }
        });
      });
    });
    return stocks;
  }, [portfolios, symbol]);

  // หาหุ้นที่ถูกเลือกจาก Dropdown ด้วย useMemo
  const selectedStock = useMemo(() => {
    if (matchingStocks.length === 0) return null;
    const found = matchingStocks.find((s) => s.subPortId === selectedSubPortId);
    return found || matchingStocks[0];
  }, [matchingStocks, selectedSubPortId]);

  // ✅ Memoize initialData แยกจาก JSX เพื่อไม่ให้สร้าง object ใหม่ทุกครั้งที่ parent re-render
  // (เช่นตอน currentPrices อัปเดตจาก polling) ซึ่งจะไปทำให้ Input ใน AddEditStock ถูกรีเซ็ตขณะพิมพ์
  const editStockInitialData = useMemo(() => {
    if (!selectedStock) return null;
    return {
      ...selectedStock,
      stockSymbol: selectedStock.stockSymbol || selectedStock.symbol // กันเหนียวฟิลด์ชื่อ
    };
  }, [selectedStock]);

  // คืนชีพ onSummaryUpdate และส่งค่ากลับไปหา HomePage
  useEffect(() => {
    if (selectedStock) {
      onSelectPortfolio(selectedStock.portId, selectedStock.subPortId);
      if (onSummaryUpdate) {
        onSummaryUpdate({
          selected: selectedStock,
          allMatches: matchingStocks,
          currentPrice: currentPrices?.[symbol] || 0
        });
      }
    } else {
      if (onSummaryUpdate) {
        onSummaryUpdate(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStock, matchingStocks, symbol, currentPrices]);

  const handleUpdateStock = async (data: { symbol: string; quantity: number; avgBuyPrice: number; ratio: number }) => {
    try {
      if (!selectedStock) return;
      const { portId, subPortId } = selectedStock;
      await updateStockInSubPortfolioTwo(portId, subPortId, selectedStock.stockSymbol, {
        stockSymbol: data.symbol,
        quantity: data.quantity,
        avgBuyPrice: data.avgBuyPrice,
        expectedRatio: data.ratio,
      });

      await refreshUserPortfolios();
      setUpdateTrigger((prev) => !prev);
      setIsEditOpen(false);
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  const handleSaveStockRatios = async (
    portfolioId: string,
    updatedRatios: { id: string; ratio: number }[]
  ) => {
    try {
      if (!selectedStock) return;
      const { subPortId } = selectedStock;

      for (const { id, ratio } of updatedRatios) {
        await updateStockInSubPortfolioTwo(portfolioId, subPortId, id, {
          stockSymbol: id,
          quantity: selectedStock.quantity,
          avgBuyPrice: selectedStock.avgBuyPrice,
          expectedRatio: ratio,
        });
      }

      await refreshUserPortfolios();
      setIsConfigStockPopupOpen(false);
    } catch (error) {
      console.error('Error saving stock ratios:', error);
    }
  };

  const calculateRemainingRatio = () => {
    if (!selectedStock) return 0;
    const portfolio = portfolios.find((p) => (p.portfolioId || p.portId || p.id) === selectedStock.portId);
    const subPort = portfolio?.subPorts?.find((sp: any) => sp.subPortId === selectedStock.subPortId);
    if (!subPort || !subPort.stocks) return 0;
    const totalRatio = subPort.stocks.reduce(
      (sum: number, stock: any) => sum + (stock.ratio ?? stock.expectedRatio ?? 0),
      0
    );
    return Math.max(0, 100 - totalRatio);
  };

  const handlePortfolioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSubPortId(e.target.value);
  };

  // ---------------- UI ท่อนล่างที่ไม่ได้แก้ใดๆ ทั้งสิ้น ---------------- 
  return (
    <div className="rounded-2xl shadow-2xl px-12 py-4 w-full max-w-lg mx-auto"
      style={{ backgroundColor: 'var(--component-bg-color)' }}
    >
      <div className="text-lg font-extrabold mb-4 tracking-tight" style={{ color: 'var(--text-color)' }}>
        {matchingStocks[0]?.isCrypto ? 'ข้อมูลเหรียญ' : 'ข้อมูลหุ้น'} <span style={{ color: 'var(--yellow-text-color)' }}>({cryptoPairLabel(symbol)})</span>
      </div>

      {matchingStocks.length === 0 ? (
        <div className="flex flex-col items-center py-2">
          <div className="text-gray-500 text-center mb-4 text-sm">ไม่พบข้อมูลหุ้นในพอร์ต</div>
          <button
            className="bg-yellow-400 text-gray-900 font-bold px-6 py-2 rounded-lg hover:bg-yellow-300 transition"
            onClick={() => navigate('/portfolio')}
          >
            +เพิ่มลงพอร์ต
          </button>
        </div>
      ) : (
        <>
          {matchingStocks.length > 1 && (
            <div className="mb-4">
              <span className="text-sm block mb-2" style={{ color: 'var(--text-color)' }}>
                {matchingStocks[0]?.isCrypto ? 'เหรียญนี้' : 'หุ้นนี้'}อยู่ในหลายพอร์ต เลือกพอร์ตที่ต้องการแสดงข้อมูล:
              </span>
              <select
                className="rounded-lg px-4 py-2 w-full border focus:outline-none focus:ring-1"
                style={{
                  backgroundColor: 'var(--bg-item)',
                  color: 'var(--text-color)',
                  border: '1px solid var(--button-bg-color)',
                  boxShadow: 'var(--box-shadow)',
                }}
                value={selectedStock?.subPortId || ''}
                onChange={handlePortfolioChange}
              >
                {matchingStocks.map((stock) => (
                  <option
                    key={stock.subPortId}
                    value={stock.subPortId}
                    style={{
                      backgroundColor: 'var(--component-bg-color)',
                      color: 'var(--text-color)',
                    }}
                  >
                    {stock.portName} - {stock.subPortName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedStock && (() => {
            // --- ส่วนคำนวณ กำไร/ขาดทุน ---
            const priceNow = currentPrices?.[symbol] || 0;
            const totalCost = selectedStock.quantity * selectedStock.avgBuyPrice;
            const currentValue = selectedStock.quantity * priceNow;
            const plAmount = priceNow > 0 ? currentValue - totalCost : 0;
            const plPercentage = (priceNow > 0 && totalCost > 0) ? (plAmount / totalCost) * 100 : 0;
            const isProfit = plAmount >= 0;
            const isCrypto = !!selectedStock.isCrypto;
            const usd = (n: number) => (isCrypto ? formatCryptoUsd(n) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
            // ---------------------------

            return (
              <div className="flex flex-col gap-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-base" style={{ color: 'var(--text-color)' }}>พอร์ต</span>
                  <span className="font-bold text-lg" style={{ color: 'var(--text-color)' }}>
                    {selectedStock.portName} - {selectedStock.subPortName}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-base" style={{ color: 'var(--text-color)' }}>{isCrypto ? 'จำนวนเหรียญ' : 'จำนวนหุ้น'}</span>
                  <span className="font-bold text-lg" style={{ color: 'var(--text-color)' }} title={isCrypto ? formatCryptoQty(selectedStock.quantityRaw) : undefined}>
                    {isCrypto
                      ? shortenDecimalText(formatCryptoQty(selectedStock.quantityRaw), 8)
                      : selectedStock.quantity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-base" style={{ color: 'var(--text-color)' }}>ต้นทุนเฉลี่ย</span>
                  <span className="font-bold text-lg" style={{ color: 'var(--text-color)' }}>
                    ${usd(selectedStock.avgBuyPrice)}
                  </span>
                </div>

                {/* --- ส่วนที่เพิ่มเข้ามา: กำไร/ขาดทุน --- */}
                {priceNow > 0 && (
                  <div className="flex justify-between items-center pt-1 border-t border-gray-700/30 mt-1">
                    <span className="text-gray-500 text-base" style={{ color: 'var(--text-color)' }}>กำไร/ขาดทุน</span>
                    <div className="text-right">
                      <div className="font-bold text-lg" style={{ color: isProfit ? 'var(--green-text-color)' : 'var(--red-text-color)' }}>
                        {isProfit ? '+' : '-'} ${usd(Math.abs(plAmount))}
                      </div>
                      <div className="text-xs font-bold" style={{ color: isProfit ? 'var(--green-text-color)' : 'var(--red-text-color)' }}>
                        {isProfit ? '▲' : '▼'} {plPercentage.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                )}
                {/* ----------------------------------- */}
                <div className="flex flex-col md:flex-row gap-2 justify-center">
                  {/* ✅ ปุ่ม ซื้อ/ขาย จะแสดงเฉพาะเมื่อ displayMode === 'V2' */}
                  {displayMode === 'V2' && (
                    <button
                      className="bg-yellow-400 text-gray-900 font-bold px-4 py-2 rounded-lg w-full md:w-2/6 hover:bg-yellow-300 transition mt-2"
                      onClick={() => {
                        openTransactionModal({
                          portId: selectedStock.portId,
                          subPortId: selectedStock.subPortId,
                          symbol: symbol,
                          currentPrice: priceNow
                        });
                      }}
                    >
                      {isCrypto ? 'ซื้อ/ขายเหรียญ' : 'ซื้อ/ขายหุ้น'}
                    </button>
                  )}

                  {/* คริปโตแก้จำนวน/ต้นทุนตรงๆ ไม่ได้ (ต้องทำผ่านรายการซื้อขาย) → พาไปหน้าพอร์ตแทน */}
                  {isCrypto ? (
                    <button
                      className="bg-yellow-400 text-gray-900 font-bold px-4 py-2 rounded-lg w-full md:w-4/6 hover:bg-yellow-300 transition mt-2"
                      onClick={() => navigate(`/portfolio/${selectedStock.portId}`)}
                    >
                      ดูพอร์ตคริปโต
                    </button>
                  ) : (
                    <button
                      className="bg-yellow-400 text-gray-900 font-bold px-4 py-2 rounded-lg w-full md:w-4/6 hover:bg-yellow-300 transition mt-2"
                      onClick={() => setIsEditOpen(true)}
                    >
                      แก้ไขหุ้นในพอร์ต
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}

      {isEditOpen && selectedStock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative">
            {/* Popup for editing stock */}
            <AddEditStock
              isOpen={isEditOpen}
              onClose={() => setIsEditOpen(false)}
              // ✅ ส่งค่า portfolioId เข้าไปตรงๆ (ไม่ต้องหาจาก portfolios ในนี้ก็ได้ถ้ามีค่าอยู่แล้ว)
              portfolioId={selectedStock.portId}
              portfolio={portfolios.find((p) => (p.portfolioId || p.portId || p.id) === selectedStock.portId)}
              subPortfolio={portfolios
                .find((p) => (p.portfolioId || p.portId || p.id) === selectedStock.portId)
                ?.subPorts?.find((sp: any) => sp.subPortId === selectedStock.subPortId)}
              initialData={editStockInitialData}
              // ✅ ส่งฟังก์ชันรีเฟรชที่เรียก refreshUserPortfolios
              onRefresh={async () => {
                await refreshUserPortfolios();
                setUpdateTrigger((prev) => !prev);
              }}
              // ไม่ต้องส่ง setIsConfigPopupOpen แล้ว เพราะเราตัดการแก้ไขสัดส่วนออก
              setIsConfigPopupOpen={setIsConfigStockPopupOpen}
              isAllocationOpen={isConfigStockPopupOpen}
            />

            {/* Popup for adjusting stock ratios */}
            {isConfigStockPopupOpen && (
              <div className="ml-4">
                <EditHoldingAllocationModal
                  isOpen={isConfigStockPopupOpen}
                  onClose={() => setIsConfigStockPopupOpen(false)}
                  portfolioId={selectedStock.portId}
                  // ส่งข้อมูลพอร์ตย่อยปัจจุบันเข้าไป
                  subPortfolio={portfolios
                    .find((p) => (p.portfolioId || p.portId || p.id) === selectedStock.portId)
                    ?.subPorts?.find((sp: any) => sp.subPortId === selectedStock.subPortId)}
                  onSave={async () => {
                    await refreshUserPortfolios();
                    setUpdateTrigger((prev) => !prev);
                  }}
                  isSideBySide={true} // ✅ เพิ่ม prop นี้เพื่อให้มันขยับไปด้านขวา
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div >
  );
};

export default ManagePortfolioV2;